/**
 * Permission string constants mapped to Django's model permission codenames.
 * Format: <app_label>.view_<model> | add_<model> | change_<model> | delete_<model>
 */

export const PERMISSIONS = {
  // Bookings
  BOOKING_VIEW: "booking.view_booking",
  BOOKING_ADD: "booking.add_booking",
  BOOKING_CHANGE: "booking.change_booking",
  BOOKING_DELETE: "booking.delete_booking",

  // Streams
  STREAM_VIEW: "stream.view_stream",
  STREAM_ADD: "stream.add_stream",
  STREAM_CHANGE: "stream.change_stream",
  STREAM_DELETE: "stream.delete_stream",

  // Venues
  VENUE_VIEW: "venue.view_venue",
  VENUE_ADD: "venue.add_venue",
  VENUE_CHANGE: "venue.change_venue",
  VENUE_DELETE: "venue.delete_venue",

  // Bug Reports
  BUG_VIEW: "bug_report.view_bug",
  BUG_ADD: "bug_report.add_bug",
  BUG_CHANGE: "bug_report.change_bug",
  BUG_DELETE: "bug_report.delete_bug",

  // Call Sheets
  CALLSHEET_VIEW: "call_sheet.view_callsheet",
  CALLSHEET_ADD: "call_sheet.add_callsheet",
  CALLSHEET_CHANGE: "call_sheet.change_callsheet",
  CALLSHEET_DELETE: "call_sheet.delete_callsheet",

  // Configurations - Thumbnails
  THUMBNAIL_VIEW: "configuration.view_thumbnail",
  THUMBNAIL_ADD: "configuration.add_thumbnail",
  THUMBNAIL_CHANGE: "configuration.change_thumbnail",
  THUMBNAIL_DELETE: "configuration.delete_thumbnail",

  // Configurations - general (using thumbnail as a proxy for "can access configurations")
  CONFIGURATION_VIEW: "configuration.view_thumbnail",
  CONFIGURATION_ADD: "configuration.add_thumbnail",
  CONFIGURATION_CHANGE: "configuration.change_thumbnail",
  CONFIGURATION_DELETE: "configuration.delete_thumbnail",

  // Chema (Donations)
  DONATION_VIEW: "chema.view_donation",
  DONATION_ADD: "chema.add_donation",
  DONATION_CHANGE: "chema.change_donation",
  DONATION_DELETE: "chema.delete_donation",

  // Crew
  CREW_VIEW: "crew.view_crewmember",

  // Equipment / Gear Management
  EQUIPMENT_VIEW: "equipment.view_equipment",

  // User Management
  USER_MANAGEMENT_VIEW: "authentication.view_customuser",
  USER_MANAGEMENT_ADD: "authentication.add_customuser",
  USER_MANAGEMENT_CHANGE: "authentication.change_customuser",
  USER_MANAGEMENT_DELETE: "authentication.delete_customuser",

  // Endpoint Requests
  ENDPOINT_REQUEST_ADMIN: "endpoint_request.delete_endpointrequest",
  ENDPOINT_REQUEST_BACKEND: "endpoint_request.change_endpointrequest",
  ENDPOINT_REQUEST_FRONTEND_ADD_REQUEST: "endpoint_request.add_endpointrequest",
  ENDPOINT_REQUEST_FRONTEND_ADD_COMPONENT: "endpoint_request.add_component",

  // Academy / LMS
  ACADEMY_VIEW: "lms.view_pixelpilotcourse",

  // Contacts
  CONTACT_VIEW: "contact.view_contact",

  // Proposals
  PROPOSAL_VIEW: "booking.view_bookingproposal",

  // Quote Requests
  QUOTE_REQUEST_VIEW: "booking.view_bookingquoterequest",

  // Memory Book / Memory Table
  MEMORY_BOOK_VIEW: "stream.view_memorybook",

  // Companies
  COMPANY_VIEW: "company.view_company",

  // Memorial Pages / Client Portal
  MEMORIAL_PAGES_VIEW: "client_portal.view_website",

  // Figma Designs
  FIGMA_VIEW: "figma_design.view_figmadesign",

  // Pixel Pilots
  PIXEL_PILOT_VIEW: "gigs.view_pixelpilotgig",

  // Notifications
  NOTIFICATION_VIEW: "notification.view_notification",

  // Comments / Memorial Messages
  COMMENT_VIEW: "chat.view_memorialmessage",

  // Memory Book Assignments
  MEMORY_BOOK_ASSIGNMENT_VIEW: "stream.view_memorybookassignment",

  // Site Visits
  SITE_VISIT_VIEW: "venue.view_sitevisit",

  // Stream Fund
  STREAM_FUND_VIEW: "streamfund.view_streamfundapplication",

  // Referral Program
  REFERRAL_VIEW: "refferal_program.view_refferalprogram",
} as const;

export type PermissionKey = keyof typeof PERMISSIONS;
export type PermissionValue = (typeof PERMISSIONS)[PermissionKey];

/**
 * The `configuration` Django app has ~29 separately-permissioned models
 * (Thumbnail, EmailTemplate, Testimonial, Site, ...). CONFIGURATION_VIEW is
 * just a proxy (configuration.view_thumbnail) for "can access the
 * Configurations section" — a user who was only granted view on a different
 * config model should still see the section, so check for any view_* grant
 * under the app rather than requiring the specific Thumbnail model.
 */
export const hasConfigurationAccess = (permissions: string[]): boolean =>
  permissions.some((p) => p.startsWith("configuration.view_"));
